import { useCallback } from 'react';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { useInstanceId } from '@frontend/hooks';
import { useSocket, useSocketChannel } from '@frontend/hooks/useSocket';
import {
  AgentExecutionResponse,
  MemoryChunk,
  MemoryChunkList,
  SpecializedAgent,
  SpecializedAgentExecution,
  SpecializedAgentListItem,
  SpecializedAgentSocketMessage,
} from '@frontend/types';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import {
  checkAgentIdAvailability,
  createSpecializedAgent,
  deleteSpecializedAgent,
  executeSpecializedAgent,
  getAgentMemoryChunks,
  getSpecializedAgent,
  getSpecializedAgentExecution,
  getSpecializedAgents,
  stopSpecializedAgentExecution,
  updateSpecializedAgent,
} from '../services/specialized-agent';

function handleApiErrorWithSentry(
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>
): string {
  const errorMessage = extractApiErrorMessage(error);

  if (!errorMessage) {
    sendSentryError(`${fallbackMessage}: No error message from API`, {
      originalError: error,
      ...context,
    });
  }

  return errorMessage || fallbackMessage;
}

export function useGetSpecializedAgentsList() {
  const { instanceId } = useInstanceId();
  const {
    data: agents,
    isPending,
    error,
  } = useQuery<SpecializedAgentListItem[]>({
    queryKey: getSpecializedAgents.key(instanceId ?? '', 1, 1000, false),
    queryFn: getSpecializedAgents({ instanceId: instanceId ?? '' }),
    enabled: !!instanceId,
  });

  return { agents: agents ?? [], isPending, error };
}

export function useGetSpecializedAgent(url: string) {
  const {
    data: agent,
    isPending,
    error,
  } = useQuery<SpecializedAgent>({
    queryKey: ['opal:specialized-agent', url],
    queryFn: getSpecializedAgent(url),
    enabled: !!url,
  });

  return { agent: agent ?? undefined, isPending, error };
}

export function useDeleteSpecializedAgent(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (deleteUrl: string) => {
      return deleteSpecializedAgent(deleteUrl)();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toaster.create('Agent deleted successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to delete agent', { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useUpdateSpecializedAgent(url: string, onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: SpecializedAgent) => {
      return updateSpecializedAgent(url, agent)();
    },
    onSuccess: (_, agent) => {
      if (agent && 'instance_id' in agent) {
        queryClient.invalidateQueries({
          queryKey: getSpecializedAgents.key(agent.instance_id as string),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agent', url] });
      toaster.create('Agent updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      console.error('Error updating specialized agent:', error);
      const errorMessage = handleApiErrorWithSentry(error, 'Failed to update agent', { url });
      toaster.create(errorMessage, { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useCreateSpecializedAgent(
  instanceId: string,
  onSuccess?: (createdAgent: SpecializedAgent) => void
) {
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: SpecializedAgent) => {
      const response = await createSpecializedAgent(agent, instanceId)();
      return response.data;
    },
    onSuccess: (createdAgent: SpecializedAgent) => {
      toaster.create('Agent created successfully', { type: 'success' });
      onSuccess?.(createdAgent);
    },
    onError: (error: unknown) => {
      console.error('Error creating specialized agent:', error);
      const errorMessage = handleApiErrorWithSentry(error, 'Failed to create agent', {
        instanceId,
      });
      toaster.create(errorMessage, { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useCheckAgentIdAvailability() {
  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({ agentId, instanceId }: { agentId: string; instanceId: string }) => {
      const result = await checkAgentIdAvailability(agentId, instanceId)();
      return { ...result, lastCheckedId: agentId };
    },
  });
  return { mutate, isPending, error, data };
}

export function useExecuteSpecializedAgent({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: AgentExecutionResponse) => void;
  onError?: () => void;
}) {
  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({
      executeUrl,
      parameters,
    }: {
      executeUrl: string;
      parameters: Record<string, unknown>;
    }) => {
      return executeSpecializedAgent(executeUrl, parameters)();
    },
    onSuccess: data => {
      onSuccess?.(data);
    },
    onError: () => {
      onError?.();
    },
  });
  return { mutate, isPending, error, data };
}

export function useGetSpecializedAgentExecution(
  instanceId: string,
  executionId: string,
  onExecutionCompleted?: () => void,
  onExecutionFailed?: () => void
) {
  const { connected: socketConnected } = useSocket();
  const {
    data: execution,
    isPending,
    error,
  } = useQuery<SpecializedAgentExecution>({
    queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
    queryFn: getSpecializedAgentExecution(instanceId, executionId),
    enabled: !!instanceId && !!executionId,
    refetchInterval: query => {
      const status = query.state?.data?.status?.toLowerCase();
      const isStreaming = status ? status !== 'completed' && status !== 'failed' : false;
      const shouldPoll = !socketConnected && isStreaming;
      return shouldPoll ? 2000 : false;
    },
  });
  const queryClient = useQueryClient();

  const handleSocketUpdate = useCallback(
    (data: unknown) => {
      const message = data as SpecializedAgentSocketMessage;
      if (message?.type === 'completion') {
        queryClient.invalidateQueries({
          queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
        });

        if (message.data.status === 'completed') {
          onExecutionCompleted?.();
        } else if (message.data.status === 'failed') {
          onExecutionFailed?.();
        }
      }
    },
    [instanceId, executionId, queryClient, onExecutionCompleted, onExecutionFailed]
  );

  useSocketChannel(`specialized_agent_execution:${executionId}`, handleSocketUpdate);

  return { execution, isPending, error };
}

export function useGetAgentMemoryChunks(
  memoryId?: string,
  memoryUrl?: string,
  isStreaming: boolean = false
) {
  const { connected: socketConnected } = useSocket();
  const shouldPoll = !socketConnected && isStreaming;

  const {
    data: memoryChunks,
    isPending,
    error,
  } = useQuery<MemoryChunkList>({
    queryKey: ['opal:agent-memory-chunks', memoryUrl, shouldPoll],
    queryFn: () => {
      if (!memoryUrl) throw new Error('memoryUrl is required');
      return getAgentMemoryChunks(memoryUrl)();
    },
    enabled: !!memoryUrl,
    refetchInterval: shouldPoll ? 2000 : false,
  });

  const queryClient = useQueryClient();

  const handleSocketUpdate = useCallback(
    (data: unknown) => {
      const message = data as SpecializedAgentSocketMessage;
      if (message?.type === 'memory_chunk') {
        queryClient.setQueryData<MemoryChunkList>(
          ['opal:agent-memory-chunks', memoryUrl, shouldPoll],
          mergeBySequence(memoryChunks ?? [], message.data.chunks)
        );
      }
    },
    [memoryUrl, queryClient, memoryChunks, shouldPoll]
  );

  useSocketChannel(`episodic_memory:${memoryId}`, handleSocketUpdate);

  return { memoryChunks: memoryChunks ?? [], isPending, error };
}

function mergeBySequence(existing: MemoryChunkList, incoming: MemoryChunkList): MemoryChunkList {
  if (!incoming?.length) return existing ?? [];
  const map = new Map<number, MemoryChunk>();
  for (const chunk of existing ?? []) map.set(chunk.sequence_number, chunk);
  for (const chunk of incoming) map.set(chunk.sequence_number, chunk);
  return Array.from(map.values()).sort((a, b) => a.sequence_number - b.sequence_number);
}

export function useStopSpecializedAgentExecution(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      instanceId,
      executionId,
      errorMessage,
    }: {
      instanceId: string;
      executionId: string;
      errorMessage: string;
    }) => {
      return stopSpecializedAgentExecution(instanceId, executionId, errorMessage)();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opal:specialized-agent-execution', variables.instanceId, variables.executionId],
      });
      toaster.create('Execution stopped successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to stop execution', { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}
